/**
 * InviteToOneDrawer — Send Aegix One invitation to a guardian or student.
 *
 * Opened from the student detail context in Reunification. Pre-fills
 * with emergency contacts from the student's EmergencyContact records,
 * or allows manual email/phone entry.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Field,
  Input,
  Text,
  VStack,
  HStack,
  Badge,
} from '@chakra-ui/react';
import { Button, SectionCard } from '@themes/aegix';
import { DrawerComponent } from '../ui/drawerComponent';
import { toaster } from '../ui/toaster';
import {
  createInvitation,
  type CreateInvitationPayload,
} from '../../services/oneInvitations.service';

interface EmergencyContact {
  contact_name: string;
  email: string;
  phone: string;
  relationship: string;
}

interface StudentInfo {
  public_id: string;
  first_name: string;
  last_name: string;
  grade?: string;
}

type DeliveryChannel = 'email' | 'sms' | 'both';

interface InviteToOneDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  student: StudentInfo;
  schoolId: string;
  emergencyContacts?: EmergencyContact[];
}

export function InviteToOneDrawer({
  isOpen,
  onClose,
  student,
  schoolId,
  emergencyContacts = [],
}: InviteToOneDrawerProps) {
  const { t } = useTranslation();
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [role, setRole] = useState<'guardian' | 'self'>('guardian');
  const [sentVia, setSentVia] = useState<DeliveryChannel>('email');
  const [loading, setLoading] = useState(false);

  const contactsWithEmail = emergencyContacts.filter(
    (c) => c.email || c.phone,
  );

  const handleSendToContact = async (
    contact: EmergencyContact,
    channel: DeliveryChannel,
  ) => {
    if (channel !== 'sms' && !contact.email) {
      toaster.error({ title: t('reunification.noEmailAddress', 'No email address for this contact') });
      return;
    }
    if (channel !== 'email' && !contact.phone) {
      toaster.error({ title: t('reunification.noPhoneNumber', 'No phone number for this contact') });
      return;
    }

    setLoading(true);
    try {
      const payload: CreateInvitationPayload = {
        recipient_email: contact.email,
        recipient_phone: contact.phone,
        recipient_name: contact.contact_name,
        role: 'guardian',
        student_ids: [student.public_id],
        school_id: schoolId,
        sent_via: channel,
      };
      await createInvitation(payload);
      toaster.success({
        title: t('reunification.invitationSent', 'Invitation sent'),
        description: t('reunification.sentToContactVia', 'Sent to {{name}} via {{channel}}', {
          name: contact.contact_name,
          channel: channelLabel(channel, t),
        }),
      });
    } catch (err) {
      toaster.error({
        title: t('reunification.failedSendInvitation', 'Failed to send invitation'),
        description:
          err instanceof Error ? err.message : t('common.pleaseTryAgain', 'Please try again.'),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendManual = async () => {
    if (sentVia !== 'sms' && !recipientEmail) return;
    if (sentVia !== 'email' && !recipientPhone) return;

    setLoading(true);
    try {
      const payload: CreateInvitationPayload = {
        recipient_email: recipientEmail,
        recipient_phone: recipientPhone,
        recipient_name: recipientName,
        role,
        student_ids: [student.public_id],
        school_id: schoolId,
        sent_via: sentVia,
      };
      await createInvitation(payload);
      toaster.success({
        title: t('reunification.invitationSent', 'Invitation sent'),
        description: t('reunification.sentViaChannel', 'Sent via {{channel}}', {
          channel: channelLabel(sentVia, t),
        }),
      });
      setRecipientEmail('');
      setRecipientPhone('');
      setRecipientName('');
    } catch (err) {
      toaster.error({
        title: t('reunification.failedSendInvitation', 'Failed to send invitation'),
        description:
          err instanceof Error ? err.message : t('common.pleaseTryAgain', 'Please try again.'),
      });
    } finally {
      setLoading(false);
    }
  };

  const isManualValid =
    (sentVia === 'email' && !!recipientEmail) ||
    (sentVia === 'sms' && !!recipientPhone) ||
    (sentVia === 'both' && !!recipientEmail && !!recipientPhone);

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={onClose}
      title={t('reunification.inviteToAegixOne', 'Invite to Aegix One')}
      size="sm"
    >
      <VStack gap={4} align="stretch">
        <SectionCard title={t('reunification.student', 'Student')}>
          <Box>
            <Text fontSize="sm" fontWeight="medium">
              {student.first_name} {student.last_name}
            </Text>
            {student.grade && (
              <Text fontSize="xs" color="text.secondary">
                {t('reunification.gradeFormat', 'Grade {{grade}}', { grade: student.grade })}
              </Text>
            )}
          </Box>
        </SectionCard>

        {contactsWithEmail.length > 0 && (
          <SectionCard title={t('reunification.emergencyContacts', 'Emergency Contacts')}>
            <VStack gap={2} align="stretch">
              {contactsWithEmail.map((contact, idx) => (
                <Box
                  key={idx}
                  p={3}
                  borderWidth="1px"
                  borderColor="border.primary"
                  borderRadius="md"
                >
                  <HStack justify="space-between" mb={1}>
                    <Box>
                      <Text fontSize="sm">{contact.contact_name}</Text>
                      <Badge size="sm" variant="outline">
                        {contact.relationship}
                      </Badge>
                    </Box>
                  </HStack>
                  {contact.email && (
                    <Text fontSize="xs" color="text.secondary">
                      {contact.email}
                    </Text>
                  )}
                  {contact.phone && (
                    <Text fontSize="xs" color="text.secondary">
                      {contact.phone}
                    </Text>
                  )}
                  <HStack gap={1} mt={2}>
                    {contact.email && (
                      <Button
                        visual="primary"
                        size="sm"
                        onClick={() => handleSendToContact(contact, 'email')}
                        disabled={loading}
                        data-e2e="email-button">
                        {t('events.email', 'Email')}
                      </Button>
                    )}
                    {contact.phone && (
                      <Button
                        visual="primary"
                        size="sm"
                        onClick={() => handleSendToContact(contact, 'sms')}
                        disabled={loading}
                        data-e2e="sms-button">
                        {t('events.sms', 'SMS')}
                      </Button>
                    )}
                    {contact.email && contact.phone && (
                      <Button
                        visual="secondary"
                        size="sm"
                        onClick={() => handleSendToContact(contact, 'both')}
                        disabled={loading}
                        data-e2e="both-button">
                        {t('reunification.both', 'Both')}
                      </Button>
                    )}
                  </HStack>
                </Box>
              ))}
            </VStack>
          </SectionCard>
        )}

        <SectionCard
          title={
            contactsWithEmail.length > 0
              ? t('reunification.orSendToSomeoneElse', 'Or send to someone else')
              : t('reunification.sendInvitation', 'Send invitation')
          }
        >
          <VStack gap={3} align="stretch">
            <Field.Root>
              <Field.Label>{t('reunification.deliveryMethod', 'Delivery Method')}</Field.Label>
              <HStack gap={2}>
                <Button
                  visual={sentVia === 'email' ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => setSentVia('email')}
                  data-e2e="email-button">
                  {t('events.email', 'Email')}
                </Button>
                <Button
                  visual={sentVia === 'sms' ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => setSentVia('sms')}
                  data-e2e="sms-button">
                  {t('events.sms', 'SMS')}
                </Button>
                <Button
                  visual={sentVia === 'both' ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => setSentVia('both')}
                  data-e2e="both-button">
                  {t('reunification.both', 'Both')}
                </Button>
              </HStack>
            </Field.Root>

            {sentVia !== 'sms' && (
              <Field.Root>
                <Field.Label>{t('events.email', 'Email')}</Field.Label>
                <Input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder={t('reunification.emailPlaceholder', 'parent@example.com')}
                  size="sm"
                />
              </Field.Root>
            )}

            {sentVia !== 'email' && (
              <Field.Root>
                <Field.Label>{t('reunification.phoneNumber', 'Phone Number')}</Field.Label>
                <Input
                  type="tel"
                  value={recipientPhone}
                  onChange={(e) => setRecipientPhone(e.target.value)}
                  placeholder={t('reunification.phonePlaceholder', '+1 555 123 4567')}
                  size="sm"
                />
              </Field.Root>
            )}

            <Field.Root>
              <Field.Label>{t('reunification.nameOptional', 'Name (optional)')}</Field.Label>
              <Input
                type="text"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder={t('reunification.recipientNamePlaceholder', 'Recipient name')}
                size="sm"
              />
            </Field.Root>

            <Field.Root>
              <Field.Label>{t('common.role', 'Role')}</Field.Label>
              <HStack gap={2}>
                <Button
                  visual={role === 'guardian' ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => setRole('guardian')}
                  data-e2e="guardian-button">
                  {t('reunification.guardian', 'Guardian')}
                </Button>
                <Button
                  visual={role === 'self' ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => setRole('self')}
                  data-e2e="student-button">
                  {t('reunification.student', 'Student')}
                </Button>
              </HStack>
            </Field.Root>

            <Button
              visual="primary"
              onClick={handleSendManual}
              disabled={loading || !isManualValid}
              data-e2e="send-invite-button">
              {loading
                ? t('common.sending', 'Sending...')
                : t('reunification.sendInvitation', 'Send invitation')}
            </Button>
          </VStack>
        </SectionCard>
      </VStack>
    </DrawerComponent>
  );
}

function channelLabel(
  channel: DeliveryChannel,
  t: (key: string, defaultValue: string) => string,
): string {
  switch (channel) {
    case 'email':
      return t('events.email', 'Email');
    case 'sms':
      return t('events.sms', 'SMS');
    case 'both':
      return t('reunification.emailAndSms', 'Email & SMS');
  }
}
